import ExcelJS from 'exceljs';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Button } from '@/components/ui/button';

interface ExportButtonProps {
  batchId: string;
  scans: any[];
  violations: any[];
}

export default function ExportButton({ batchId, scans, violations }: ExportButtonProps) {
  const visibilityByBatch: any[] | undefined = useQuery(api.searchVisibility.getVisibilityDataByBatch, { batchId });
  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();

    workbook.creator = 'OSN Piracy Detection System';
    workbook.created = new Date();
    workbook.modified = new Date();

    // Summary Sheet
    const summarySheet = workbook.addWorksheet('Summary');
    summarySheet.columns = [
      { header: 'Content Title', key: 'title', width: 35 },
      { header: 'Content Type', key: 'type', width: 15 },
      { header: 'Year', key: 'year', width: 10 },
      { header: 'Priority', key: 'priority', width: 12 },
      { header: 'Violations Found', key: 'count', width: 18 },
      { header: 'Visibility Score', key: 'visibilityScore', width: 17 },
      { header: 'Visibility Rating', key: 'visibilityRating', width: 20 },
      { header: 'OSN+ Rank', key: 'osnRank', width: 12 },
      { header: 'Pirates Before OSN+', key: 'piratesBeforeOsn', width: 20 },
      { header: 'Highest Risk Domain', key: 'topDomain', width: 40 },
      { header: 'Max Confidence', key: 'maxScore', width: 15 },
      { header: 'Status', key: 'status', width: 12 },
    ];

    // Style header row
    summarySheet.getRow(1).font = { bold: true };
    summarySheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF6366F1' },
    };
    summarySheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };

    // Helper function to get visibility rating
    const getVisibilityRating = (score: number | null | undefined): string => {
      if (score === null || score === undefined) return 'Not Available';
      if (score >= 90) return 'Excellent';
      if (score >= 80) return 'Very Good';
      if (score >= 70) return 'Good';
      if (score >= 50) return 'Moderate';
      if (score >= 30) return 'Poor';
      return 'Critical';
    };

    // Add summary data
    for (const scan of scans) {
      const scanViolations = violations.filter(v => v.scanId === scan._id);
      const topViolation = scanViolations.sort((a, b) => b.confidenceScore - a.confidenceScore)[0];

      const row = summarySheet.addRow({
        title: scan.contentTitle,
        type: scan.contentType || 'N/A',
        year: scan.contentYear || 'N/A',
        priority: scan.priority || 'N/A',
        count: scan.violationCount,
        visibilityScore: scan.visibilityScore !== undefined && scan.visibilityScore !== null ? scan.visibilityScore.toFixed(1) : 'N/A',
        visibilityRating: getVisibilityRating(scan.visibilityScore),
        osnRank: scan.osnRank || 'N/A',
        piratesBeforeOsn: scan.piratesBeforeOsn !== undefined && scan.piratesBeforeOsn !== null ? scan.piratesBeforeOsn : 'N/A',
        topDomain: topViolation?.domain || 'N/A',
        maxScore: topViolation?.confidenceScore || 0,
        status: scan.status,
      });

      // Color code visibility score
      if (scan.visibilityScore !== undefined && scan.visibilityScore !== null) {
        const scoreCell = row.getCell('visibilityScore');
        if (scan.visibilityScore >= 90) {
          scoreCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } };
        } else if (scan.visibilityScore >= 80) {
          scoreCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
        } else if (scan.visibilityScore >= 70) {
          scoreCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
        } else if (scan.visibilityScore >= 50) {
          scoreCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFED7AA' } };
        } else if (scan.visibilityScore >= 30) {
          scoreCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECACA' } };
        } else {
          scoreCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECACA' } };
        }
      }
    }

    // Violations Sheet
    const violationsSheet = workbook.addWorksheet('Violations');
    violationsSheet.columns = [
      { header: 'Content Title', key: 'contentTitle', width: 30 },
      { header: 'URL', key: 'url', width: 60 },
      { header: 'Domain', key: 'domain', width: 35 },
      { header: 'Confidence Score', key: 'score', width: 17 },
      { header: 'Confidence Level', key: 'level', width: 17 },
      { header: 'Verified', key: 'verified', width: 12 },
      { header: 'Verification Method', key: 'verificationMethod', width: 20 },
      { header: 'Violation Signals', key: 'signals', width: 45 },
      { header: 'Description', key: 'blurb', width: 50 },
      { header: 'Page Title', key: 'pageTitle', width: 40 },
      { header: 'Hosting Provider', key: 'hostingProvider', width: 30 },
      { header: 'IP Address', key: 'ipAddress', width: 18 },
      { header: 'Server Location', key: 'serverLocation', width: 30 },
      { header: 'Hosting Cached', key: 'hostingCached', width: 16 },
      { header: 'Hosting Lookup Date', key: 'hostingLookupDate', width: 24 },
      { header: 'ASN', key: 'asn', width: 15 },
      { header: 'Hosting Abuse Email', key: 'hostingAbuseEmail', width: 35 },
      { header: 'Registrar Abuse Email', key: 'registrarAbuseEmail', width: 35 },
      { header: 'All Abuse Emails', key: 'allAbuseEmails', width: 60 },
      { header: 'Registrar', key: 'registrar', width: 25 },
      { header: 'Registration Date', key: 'registrationDate', width: 18 },
      { header: 'WHOIS Privacy', key: 'whoisPrivacy', width: 15 },
      { header: 'Screenshot URL', key: 'screenshot', width: 60 },
      { header: 'Detected At', key: 'detectedAt', width: 20 },
      { header: 'Reviewed', key: 'reviewed', width: 12 },
      { header: 'Reachable SA', key: 'reachableSA', width: 15 },
      { header: 'Reachable AE', key: 'reachableAE', width: 15 },
      { header: 'Reachable EG', key: 'reachableEG', width: 15 },
    ];

    // Style header row
    violationsSheet.getRow(1).font = { bold: true };
    violationsSheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFEF4444' },
    };
    violationsSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };

    // Add violation data
    for (const violation of violations) {
      const scan = scans.find(s => s._id === violation.scanId);
      const level = getConfidenceLevel(violation.confidenceScore);

      const row = violationsSheet.addRow({
        contentTitle: scan?.contentTitle || 'Unknown',
        url: violation.url,
        domain: violation.domain,
        score: violation.confidenceScore,
        level: level,
        verified: violation.verified === true ? 'Yes' : (violation.verified === false ? 'No' : 'N/A'),
        verificationMethod: violation.verificationMethod || 'N/A',
        signals: violation.signals.join(', '),
        blurb: violation.blurb,
        pageTitle: violation.pageTitle || 'N/A',
        hostingProvider: violation.hostingProvider || 'N/A',
        ipAddress: violation.ipAddress || 'N/A',
        serverLocation: violation.serverLocation || 'N/A',
        hostingCached: violation.hostingCached ? 'Yes' : 'No',
        hostingLookupDate: violation.hostingLookupDate ? new Date(violation.hostingLookupDate).toLocaleString() : 'N/A',
        asn: violation.asn ? `${violation.asn} (${violation.asnOrg || 'Unknown'})` : 'N/A',
        hostingAbuseEmail: violation.hostingAbuseEmail || 'N/A',
        registrarAbuseEmail: violation.registrarAbuseEmail || 'N/A',
        allAbuseEmails: violation.abuseEmails ? violation.abuseEmails.join(', ') : (violation.abuseEmail || 'N/A'),
        registrar: violation.registrar || 'N/A',
        registrationDate: violation.registrationDate || 'N/A',
        whoisPrivacy: violation.whoisPrivacy ? 'Yes' : 'No',
        screenshot: violation.screenshotUrl || 'N/A',
        detectedAt: new Date(violation.detectedAt).toLocaleString(),
        reviewed: violation.reviewed ? 'Yes' : 'No',
        reachableSA: violation.reachableSAStatus || 'Unknown',
        reachableAE: violation.reachableAEStatus || 'Unknown',
        reachableEG: violation.reachableEGStatus || 'Unknown',
      });

      // Color code by confidence level
      if (violation.confidenceScore >= 85) {
        row.getCell('score').fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFEE2E2' },
        };
      } else if (violation.confidenceScore >= 70) {
        row.getCell('score').fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFED7AA' },
        };
      }
    }

    // Visibility (Per-Query) Sheet
    const visSheet = workbook.addWorksheet('Visibility');
    visSheet.columns = [
      { header: 'Content Title', key: 'contentTitle', width: 30 },
      { header: 'Query', key: 'query', width: 60 },
      { header: 'SQS', key: 'score', width: 10 },
      { header: 'OSN Rank', key: 'osnRank', width: 12 },
      { header: 'Pirates Before OSN', key: 'piratesBeforeOsn', width: 20 },
      { header: 'Total Pirates', key: 'totalPirates', width: 15 },
      { header: 'Alpha', key: 'alpha', width: 8 },
      { header: 'Beta', key: 'beta', width: 8 },
      { header: 'OSN URLs', key: 'osnUrls', width: 50 },
      { header: 'Pirate URLs', key: 'pirateUrls', width: 50 },
      { header: 'Timestamp', key: 'ts', width: 22 },
    ];
    visSheet.getRow(1).font = { bold: true };
    visSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF22c55e' } };
    visSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };

    if (visibilityByBatch && visibilityByBatch.length > 0) {
      for (const vd of visibilityByBatch) {
        const scan = scans.find(s => s._id === vd.scanId);
        const row = visSheet.addRow({
          contentTitle: scan?.contentTitle || 'Unknown',
          query: vd.query,
          score: vd.visibilityScore,
          osnRank: vd.osnRank ?? 'N/A',
          piratesBeforeOsn: vd.piratesBeforeOsn,
          totalPirates: vd.totalPirates,
          alpha: vd.alpha,
          beta: vd.beta,
          osnUrls: (vd.osnUrls || []).map((u: any) => `#${u.rank} ${u.url} (${u.source || '?'})`).join('  |  '),
          pirateUrls: (vd.pirateUrls || []).slice(0, 10).map((u: any) => `#${u.rank} ${u.domain} (${u.source || '?'})`).join('  |  '),
          ts: vd.timestamp ? new Date(vd.timestamp).toLocaleString() : 'N/A',
        });
        if (typeof vd.visibilityScore === 'number') {
          const c = row.getCell('score');
          const s = vd.visibilityScore;
          c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: s >= 80 ? 'FFDCFCE7' : s >= 50 ? 'FFFEF3C7' : 'FFFECACA' } };
        }
      }
    }

    // Domain Summary Sheet
    const domainSheet = workbook.addWorksheet('Domain Summary');
    domainSheet.columns = [
      { header: 'Domain', key: 'domain', width: 40 },
      { header: 'Violation Count', key: 'count', width: 17 },
      { header: 'Avg Confidence', key: 'avgScore', width: 17 },
      { header: 'Content Titles', key: 'titles', width: 50 },
    ];

    // Style header row
    domainSheet.getRow(1).font = { bold: true };
    domainSheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF8B5CF6' },
    };
    domainSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };

    // Aggregate by domain
    const domainMap = new Map<string, any>();

    for (const violation of violations) {
      const scan = scans.find(s => s._id === violation.scanId);
      const domain = violation.domain;

      if (!domainMap.has(domain)) {
        domainMap.set(domain, {
          domain,
          count: 0,
          totalScore: 0,
          titles: new Set<string>(),
        });
      }

      const domainData = domainMap.get(domain);
      domainData.count++;
      domainData.totalScore += violation.confidenceScore;
      if (scan) {
        domainData.titles.add(scan.contentTitle);
      }
    }

    // Add domain summary data
    const sortedDomains = Array.from(domainMap.values()).sort((a, b) => b.count - a.count);

    for (const domainData of sortedDomains) {
      domainSheet.addRow({
        domain: domainData.domain,
        count: domainData.count,
        avgScore: Math.round(domainData.totalScore / domainData.count),
        titles: Array.from(domainData.titles).join(', '),
      });
    }

    // Hosting Summary Sheet
    const hostingSheet = workbook.addWorksheet('Hosting Summary');
    hostingSheet.columns = [
      { header: 'Hosting Provider', key: 'provider', width: 35 },
      { header: 'Violation Count', key: 'count', width: 17 },
      { header: 'Domains', key: 'domains', width: 50 },
      { header: 'Hosting Abuse Email', key: 'hostingAbuse', width: 35 },
      { header: 'Registrar Abuse Email', key: 'registrarAbuse', width: 35 },
      { header: 'Server Locations', key: 'locations', width: 40 },
    ];

    // Style header row
    hostingSheet.getRow(1).font = { bold: true };
    hostingSheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF10b981' },
    };
    hostingSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };

    // Aggregate by hosting provider
    const hostingMap = new Map<string, any>();

    for (const violation of violations) {
      const provider = violation.hostingProvider || 'Unknown';

      if (!hostingMap.has(provider)) {
        hostingMap.set(provider, {
          provider,
          count: 0,
          domains: new Set<string>(),
          hostingAbuseEmails: new Set<string>(),
          registrarAbuseEmails: new Set<string>(),
          locations: new Set<string>(),
        });
      }

      const hostingData = hostingMap.get(provider);
      hostingData.count++;
      hostingData.domains.add(violation.domain);

      if (violation.hostingAbuseEmail) {
        hostingData.hostingAbuseEmails.add(violation.hostingAbuseEmail);
      }
      if (violation.registrarAbuseEmail) {
        hostingData.registrarAbuseEmails.add(violation.registrarAbuseEmail);
      }
      if (violation.serverLocation) {
        hostingData.locations.add(violation.serverLocation);
      }
    }

    // Add hosting summary data
    const sortedHosting = Array.from(hostingMap.values()).sort((a, b) => b.count - a.count);

    for (const hostingData of sortedHosting) {
      hostingSheet.addRow({
        provider: hostingData.provider,
        count: hostingData.count,
        domains: Array.from(hostingData.domains).join(', '),
        hostingAbuse: Array.from(hostingData.hostingAbuseEmails).join(', ') || 'N/A',
        registrarAbuse: Array.from(hostingData.registrarAbuseEmails).join(', ') || 'N/A',
        locations: Array.from(hostingData.locations).join(', ') || 'N/A',
      });
    }

    // Unverified (Search-Only) Sheet
    const unverified = violations.filter(v => v.verified === false || v.verificationMethod === 'search_only');
    const unverifiedSheet = workbook.addWorksheet('Unverified');
    unverifiedSheet.columns = [
      { header: 'Content Title', key: 'contentTitle', width: 30 },
      { header: 'URL', key: 'url', width: 60 },
      { header: 'Domain', key: 'domain', width: 35 },
      { header: 'Confidence Score', key: 'score', width: 18 },
      { header: 'Verification Method', key: 'verificationMethod', width: 22 },
      { header: 'Signals', key: 'signals', width: 40 },
      { header: 'Description', key: 'blurb', width: 50 },
      { header: 'Detected At', key: 'detectedAt', width: 22 },
      { header: 'Hosting Cached', key: 'hostingCached', width: 16 },
      { header: 'Hosting Lookup Date', key: 'hostingLookupDate', width: 24 },
    ];
    unverifiedSheet.getRow(1).font = { bold: true };
    unverifiedSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFf59e0b' } };
    unverifiedSheet.getRow(1).font = { bold: true, color: { argb: 'FF111827' } };
    for (const v of unverified) {
      const scan = scans.find(s => s._id === v.scanId);
      const row = unverifiedSheet.addRow({
        contentTitle: scan?.contentTitle || 'Unknown',
        url: v.url,
        domain: v.domain,
        score: v.confidenceScore,
        verificationMethod: v.verificationMethod || 'search_only',
        signals: (v.signals || []).join(', '),
        blurb: v.blurb,
        detectedAt: new Date(v.detectedAt).toLocaleString(),
        hostingCached: v.hostingCached ? 'Yes' : 'No',
        hostingLookupDate: v.hostingLookupDate ? new Date(v.hostingLookupDate).toLocaleString() : '',
      });
      // Light warning style for unverified
      row.getCell('score').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
    }

    // Download the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `piracy-report-${batchId}-${Date.now()}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const getConfidenceLevel = (score: number): string => {
    if (score >= 85) return 'Very High';
    if (score >= 70) return 'High';
    if (score >= 60) return 'Medium';
    return 'Low';
  };

  return (
    <Button onClick={handleExport} variant="secondary" className="inline-flex items-center border px-4 py-2 rounded-md bg-background hover:bg-accent">
      📥 Export Report
    </Button>
  );
}
